import { memo, useEffect, useState } from 'react';
import {
  Grid,
  TableContainer,
  Table,
  TableBody,
  TableRow,
  TableCell,
  Typography,
} from '@mui/material';
import { useAppSelector } from 'app/hooks';
import { useImperialMeasurements } from 'features/weatherWalls/hooks/useImperialMeasurements';
import { Weather } from '../../types/models/Weather';
import {
  convertDBNumber,
  appendUnitsToNumber,
  getUnitForMeasurementType,
} from './utils/dataConverters';
import { LocationWeatherRelations, WeatherConstraint } from 'types';
import { WeatherAlertCurrentConditions } from './types';
import WeatherAlert from './WeatherAlert';
import { getWeatherAlerts } from 'features/weatherAlerts/utils/alertCheckers';
import { colorCell } from 'features/weatherReflection/utils/cellFormatters';
import WeatherStationsIcon from 'features/locations/WeatherStationsIcon';
import { useDishesByAnalyzer } from 'features/dishes/api/getDishesByAnalyzer';
import { useDishes } from 'features/dishes/api/getDishes';
import { useUserDateTime } from '../timezone/hooks/useUserDateTime';
import { formatInTimeZone } from 'date-fns-tz';

interface Props {
  currentConditions: Weather | undefined;
  location: LocationWeatherRelations | null | undefined;
  inTraceHistory?: boolean;
  analyzerId: number | undefined;
  weatherConstraints?: WeatherConstraint[];
}

const CurrentConditions = memo(
  ({
    currentConditions,
    location,
    inTraceHistory,
    analyzerId,
    weatherConstraints,
  }: Props) => {
    const [weatherAlerts, setWeatherAlerts] =
      useState<WeatherAlertCurrentConditions>({});
    const { dateFormat, timeFormat, timezone } = useUserDateTime();
    const useImperial = useAppSelector(
      (state) => state.auth.user?.us_customary_units
    );
    const { data: analyzerDishes } = useDishesByAnalyzer({
      analyzerId: analyzerId ? analyzerId : -1,
    });
    const { data: allDishes } = useDishes({});
    useEffect(() => {
      const dishes = analyzerId
        ? analyzerDishes
        : allDishes?.filter((dish) => dish.location_id === location?.id);
      if (!dishes) return;
      const newAlerts = getWeatherAlerts(
        dishes,
        currentConditions,
        useImperial
      );
      setWeatherAlerts(newAlerts);
    }, [
      analyzerDishes,
      allDishes,
      analyzerId,
      currentConditions,
      useImperial,
      location?.id,
    ]);

    const [convertedCurrentConditions] = useImperialMeasurements(
      currentConditions ? [currentConditions] : []
    );
    if (!currentConditions)
      return (
        <Grid item style={{ padding: '2rem' }}>
          <Typography color='GrayText'>
            {inTraceHistory
              ? 'No historical weather data within 15 minutes of the selected trace. To see historical weather, the selected analyzer must be paired with a location, and that location must be enabled.'
              : 'Select an analyzer to view current weather conditions.'}
          </Typography>
        </Grid>
      );
    return (
      <Grid item>
        <Grid item container justifyContent='center'>
          {location?.weather_station_id && (
            <WeatherStationsIcon
              stationId={location.weather_station_id}
              sx={{ mr: '.5rem' }}
            />
          )}
          <Typography align='center'>
            {inTraceHistory ? 'Historical' : 'Current'} Conditions
          </Typography>
        </Grid>
        <TableContainer>
          <Table aria-label='simple table'>
            <TableBody>
              <TableRow
                sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
              >
                <TableCell component='th' scope='row'>
                  Location
                </TableCell>
                <TableCell>{location?.name ? location.name : 'None'}</TableCell>
              </TableRow>
              <TableRow
                sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
              >
                <TableCell component='th' scope='row'>
                  {inTraceHistory ? 'Closest Weather Record' : 'Last Updated'}
                </TableCell>
                <TableCell>
                  {formatInTimeZone(
                    new Date(convertedCurrentConditions.time),
                    timezone,
                    `${dateFormat} ${timeFormat}`
                  )}
                </TableCell>
              </TableRow>
              <TableRow
                sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
              >
                <TableCell component='th' scope='row'>
                  Condition
                </TableCell>
                <TableCell>
                  {convertedCurrentConditions.condition
                    ? convertedCurrentConditions.condition
                    : 'Not available'}
                </TableCell>
              </TableRow>
              <TableRow
                sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
              >
                <TableCell
                  component='th'
                  scope='row'
                  sx={colorCell(weatherConstraints, 'temp_c')}
                >
                  Temperature
                </TableCell>
                <TableCell sx={colorCell(weatherConstraints, 'temp_c')}>
                  {convertDBNumber(
                    convertedCurrentConditions.temp_c,
                    getUnitForMeasurementType('temp_c', useImperial)
                  )}
                </TableCell>
              </TableRow>
              <TableRow
                sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
              >
                <TableCell
                  component='th'
                  scope='row'
                  sx={colorCell(weatherConstraints, 'wind_kph')}
                >
                  Wind Speed
                </TableCell>
                <TableCell sx={colorCell(weatherConstraints, 'wind_kph')}>
                  {convertDBNumber(
                    convertedCurrentConditions.wind_kph,
                    getUnitForMeasurementType('wind_kph', useImperial)
                  )}
                  {' (' + convertedCurrentConditions.wind_dir + ')'}
                  {weatherAlerts['wind_kph'] && (
                    <WeatherAlert
                      condition='wind_kph'
                      weatherAlerts={weatherAlerts}
                    />
                  )}
                </TableCell>
              </TableRow>
              <TableRow
                sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
              >
                <TableCell
                  component='th'
                  scope='row'
                  sx={colorCell(weatherConstraints, 'gust_kph')}
                >
                  Wind Gust
                </TableCell>
                <TableCell sx={colorCell(weatherConstraints, 'gust_kph')}>
                  {convertDBNumber(
                    convertedCurrentConditions.gust_kph,
                    getUnitForMeasurementType('gust_kph', useImperial)
                  )}
                  {weatherAlerts['gust_kph'] && (
                    <WeatherAlert
                      condition='gust_kph'
                      weatherAlerts={weatherAlerts}
                    />
                  )}
                </TableCell>
              </TableRow>
              <TableRow
                sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
              >
                <TableCell
                  component='th'
                  scope='row'
                  sx={colorCell(weatherConstraints, 'cloud_percent')}
                >
                  Cloud Cover Percentage
                </TableCell>
                <TableCell sx={colorCell(weatherConstraints, 'cloud_percent')}>
                  {appendUnitsToNumber(
                    convertedCurrentConditions.cloud_percent,
                    '%'
                  )}
                  {weatherAlerts['cloud_percent'] && (
                    <WeatherAlert
                      condition='cloud_percent'
                      weatherAlerts={weatherAlerts}
                    />
                  )}
                </TableCell>
              </TableRow>
              <TableRow
                sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
              >
                <TableCell
                  component='th'
                  scope='row'
                  sx={colorCell(weatherConstraints, 'dewpoint_c')}
                >
                  Dewpoint
                </TableCell>
                <TableCell sx={colorCell(weatherConstraints, 'dewpoint_c')}>
                  {convertDBNumber(
                    convertedCurrentConditions.dewpoint_c,
                    getUnitForMeasurementType('dewpoint_c', useImperial)
                  )}
                </TableCell>
              </TableRow>
              <TableRow
                sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
              >
                <TableCell
                  component='th'
                  scope='row'
                  sx={colorCell(weatherConstraints, 'humidity')}
                >
                  Relative Humidity
                </TableCell>
                <TableCell sx={colorCell(weatherConstraints, 'humidity')}>
                  {appendUnitsToNumber(
                    convertedCurrentConditions.humidity,
                    '%'
                  )}
                </TableCell>
              </TableRow>
              <TableRow
                sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
              >
                <TableCell
                  component='th'
                  scope='row'
                  sx={colorCell(weatherConstraints, 'precip_mm')}
                >
                  Precipitation
                </TableCell>
                <TableCell sx={colorCell(weatherConstraints, 'precip_mm')}>
                  {convertDBNumber(
                    convertedCurrentConditions.precip_mm,
                    getUnitForMeasurementType('precip_mm', useImperial)
                  )}
                  {weatherAlerts['precip_mm'] && (
                    <WeatherAlert
                      condition='precip_mm'
                      weatherAlerts={weatherAlerts}
                    />
                  )}
                </TableCell>
              </TableRow>
              <TableRow
                sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
              >
                <TableCell
                  component='th'
                  scope='row'
                  sx={colorCell(weatherConstraints, 'pressure_mb')}
                >
                  Barometric Pressure
                </TableCell>
                <TableCell sx={colorCell(weatherConstraints, 'pressure_mb')}>
                  {convertDBNumber(
                    convertedCurrentConditions.pressure_mb,
                    getUnitForMeasurementType('pressure_mb', useImperial)
                  )}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Grid>
    );
  }
);

export default CurrentConditions;
